import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[25]

export const KWP_25_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '25-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-25',
  title: '25 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 25-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für große Wohnobjekte.',
  keywords: [
    '25 kWp Photovoltaik Kosten',
    '25 kWp Solaranlage Preis',
    'Photovoltaikanlage 25 kWp',
    'Was kostet eine 25 kWp Anlage',
    '25 kWp PV-Anlage Mehrfamilienhaus',
  ],
  eyebrow: 'Anlagengröße 25 kWp',
  h1: '25 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    'Eine 25-kWp-Anlage eignet sich für große Wohnobjekte mit mehreren Parteien oder sehr hohem ' +
    'Gesamtverbrauch - und liegt genau an der Grenze, ab der sich 2026 das EEG-Vergütungssystem ' +
    'ändert. Hier finden Sie Richtwerte zu Dachfläche und Modulanzahl, eine Kostenaufschlüsselung ' +
    'und direkt darunter einen Rechner mit dem für 25 kWp vorbelegten Durchschnittspreis aus ' +
    'echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 25 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 25-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 25 kWp passen typischerweise zu einem größeren Mehrfamilienhaus mit mehreren ' +
        'Parteien oder einem Wohnobjekt mit sehr hohem Gesamtverbrauch, Jahresverbrauch insgesamt ' +
        'oft über 16.000 kWh. Bei mehreren Parteien im selben Gebäude klärt der Handwerksbetrieb ' +
        'zusätzlich Fragen der Verbrauchsaufteilung.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 25-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Bei dieser Größe verteilen ` +
        'Handwerksbetriebe die Module meist auf mehrere Dachflächen oder -abschnitte - eine genaue, ' +
        'dachspezifische Berechnung liefert der kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 25 kWp meist mehrere Strings und Wechselrichter',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung über mehrere Dachabschnitte',
        'Gerüst: bei größerer Dachfläche entsprechend umfangreicher',
        'Elektroinstallation: Zählerschrank-Anpassung, ggf. mehrere Zähler bei mehreren Parteien',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        'Bei Wohnobjekten mit mehreren Parteien lohnt sich vorab eine Klärung, ob eine ' +
        'Mieterstrom-Lösung oder eine reine Eigenversorgung des Gebäudes sinnvoller ist - das ' +
        'beeinflusst sowohl die technische Auslegung als auch die Kostenstruktur.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        '2026 ist das letzte Jahr der garantierten EEG-Festvergütung für neue Anlagen unter ' +
        '25 kWp - wer eine 25-kWp-Anlage 2026 in Betrieb nimmt, fällt bereits unter das ab dieser ' +
        'Grenze geänderte Vergütungssystem. Bis 30 kWp bleiben Einnahmen aus Photovoltaikanlagen ' +
        'weiterhin vollständig einkommensteuerbefreit, ohne Gewerbeanmeldung. Details dazu im ' +
        'Förder-Überblick, eine genaue Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Was bedeutet die 25-kWp-Grenze bei der EEG-Förderung 2026?',
      a: '2026 ist das letzte Jahr der garantierten EEG-Festvergütung für neue Anlagen unter 25 kWp - für Anlagen ab 25 kWp gilt bereits ein geändertes Vergütungssystem. Details dazu im Förder-Überblick.',
    },
    {
      q: 'Brauche ich bei 25 kWp eine Gewerbeanmeldung?',
      a: 'Nein - Einnahmen und Entnahmen aus Photovoltaikanlagen bis 30 kWp sind vollständig einkommensteuerbefreit, ohne Gewerbeanmeldung und ohne Einnahmen-Überschuss-Rechnung. 25 kWp liegt innerhalb dieser Grenze.',
    },
    {
      q: 'Für welche Gebäude eignet sich eine 25-kWp-Anlage?',
      a: 'Typischerweise für größere Mehrfamilienhäuser mit mehreren Parteien oder Wohnobjekte mit sehr hohem Gesamtverbrauch. Bei mehreren Parteien klärt der Handwerksbetrieb zusätzlich Fragen der Verbrauchsaufteilung bzw. einer möglichen Mieterstrom-Lösung.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '20 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/20-kwp-photovoltaik-kosten' },
    { label: '15 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/15-kwp-photovoltaik-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
  ],
}
